import type { Utilisateur } from '@/shared/auth/types';
import type {
  Chantier,
  Contact,
  ContactPartenaire,
  DocumentConformite,
  DocumentEntreprise,
  Entreprise,
  EtapeChantier,
  ModeleEtape,
  Partenaire,
  Preuve,
  PreuveSav,
  Relance,
  Site,
  Ticket,
  TypeDocument,
} from '@/shared/donnees/types';
import { DOCUMENTS_DE_DEPART } from '@/shared/referentiel/chantier';
/* `Site` sert aussi à la montée de version, plus bas. */
import { graine, POSITIONS_DEPART } from './graine';
import { graineSav, modeleDeDepart } from './graine-chantiers';

/**
 * Les données de l'accès libre, gardées dans le navigateur.
 *
 * Tant que le projet Supabase n'existe pas, l'application tourne là-dessus :
 * on crée, modifie, supprime, et tout survit au rechargement. « Réinitialiser »
 * remet le jeu de départ. Rien de ceci n'atteint un build de production.
 */
export interface EntreeJournalDemo {
  id: number;
  horodatage: string;
  acteurNom: string | null;
  action: 'creation' | 'modification' | 'suppression';
  objetType: string;
  objetId: string;
  libelle: string | null;
  changements: Record<string, [unknown, unknown]>;
}

/** Un chantier tel qu'il est rangé : les noms (entreprise, site) se lisent à la demande. */
export type ChantierDemo = Pick<
  Chantier,
  'id' | 'reference' | 'entrepriseId' | 'siteId' | 'titre' | 'batiment' | 'notes' | 'creeLe'
> & { partenaireId?: string | null };

/** Une étape rangée : le nom du partenaire et les preuves se lisent à la demande. */
export type EtapeDemo = Omit<EtapeChantier, 'partenaireNom' | 'preuves' | 'enAttenteDe'> & {
  /** L'étape du même chantier qu'elle attend ; l'attente se calcule à la lecture. */
  attend?: string | null;
};

/** Un ticket rangé : ce qui vient du chantier et du partenaire se lit à la demande. */
export type TicketDemo = Omit<
  Ticket,
  | 'chantierReference'
  | 'entrepriseId'
  | 'entrepriseNom'
  | 'siteNom'
  | 'commune'
  | 'departement'
  | 'position'
  | 'partenaireNom'
>;

export interface EtatDemo {
  version: 6;
  entreprises: Entreprise[];
  sites: Site[];
  contacts: Contact[];
  /** Absent des démos enregistrées avant les contacts partenaires : vide alors. */
  contactsPartenaires?: ContactPartenaire[];
  documentsEntreprise: (DocumentEntreprise & { entrepriseId: string })[];
  partenaires: Partenaire[];
  conformite: (DocumentConformite & { partenaireId: string })[];
  comptes: Utilisateur[];
  journal: EntreeJournalDemo[];
  modele: ModeleEtape[];
  typesDocument: TypeDocument[];
  chantiers: ChantierDemo[];
  etapes: EtapeDemo[];
  preuves: Preuve[];
  tickets: TicketDemo[];
  preuvesSav: PreuveSav[];
  relances: Relance[];
}

const CLE_ETAT = 'vla-demo-etat';
const CLE_COMPTE = 'vla-demo-compte';
const CLE_FICHIER = 'vla-demo-fichier:';

let etat: EtatDemo | null = null;
const abonnes = new Set<() => void>();

function stockage(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

export function lireEtat(): EtatDemo {
  if (etat) return etat;
  const brut = stockage()?.getItem(CLE_ETAT);
  if (brut) {
    try {
      /* Une démo d'une version précédente garde ce qu'on y a saisi : on la
         monte d'un cran à la fois jusqu'à la version courante. */
      let lu = JSON.parse(brut) as Record<string, unknown> & { version?: unknown };
      const version = lu.version;
      if (lu.version === 1) {
        /* Avant S3 : on ajoute les chantiers du jeu de départ. */
        const depart = graine();
        lu = {
          ...lu,
          version: 2,
          modele: depart.modele,
          chantiers: depart.chantiers,
          etapes: depart.etapes,
          preuves: depart.preuves,
        };
      }
      if (lu.version === 2) {
        /* Avant S5 : les sites reçoivent une position, connue pour ceux du jeu de départ. */
        const sites = (lu.sites ?? []) as Omit<Site, 'position'>[];
        lu = {
          ...lu,
          version: 3,
          sites: sites.map((s) => ({ ...s, position: POSITIONS_DEPART[s.id] ?? null })),
        };
      }
      if (lu.version === 3) {
        /* Avant S6 : les étapes n'avaient pas de réserve. */
        const etapes = (lu.etapes ?? []) as Omit<EtapeDemo, 'reserve'>[];
        lu = { ...lu, version: 4, etapes: etapes.map((e) => ({ ...e, reserve: null })) };
      }
      if (lu.version === 4) {
        /* Avant S8 et S9 : ni SAV ni relances, et les validations passées sont
           tenues pour contrôlées — sinon tout l'historique remplirait « À traiter ». */
        const etapes = (lu.etapes ?? []) as Omit<EtapeDemo, 'controleeLe' | 'controleePar'>[];
        const chantiers = new Set(((lu.chantiers ?? []) as ChantierDemo[]).map((c) => c.id));
        const depart = graineSav();
        const tickets = depart.tickets.filter((t) => chantiers.has(t.chantierId));
        const ids = new Set(tickets.map((t) => t.id));
        lu = {
          ...lu,
          version: 5,
          etapes: etapes.map((e) => ({
            ...e,
            controleeLe: e.statut === 'validee' ? e.valideeLe : null,
            controleePar: null,
          })),
          tickets,
          preuvesSav: depart.preuvesSav.filter((p) => ids.has(p.ticketId)),
          relances: [],
        };
      }
      if (lu.version === 5) {
        /* Avant S10 : cinq étapes fixes, nommées par leur métier, et un contrôle
           après validation. La chaîne devient libre ; le contrôle disparaît. */
        const ancien: Record<string, [number, string, string]> = {
          livraison: [1, 'Réception de matériel', 'camion'],
          solaire: [2, 'Installation solaire', 'soleil'],
          batiment: [3, 'Aménagement du bâtiment', 'batiment'],
          sechage: [4, 'Installation du système de séchage', 'ventilateur'],
          reception: [5, 'Réception du chantier — visite finale', 'drapeau'],
        };
        const etapes = (lu.etapes ?? []) as (Record<string, unknown> & { etape: string })[];
        lu = {
          ...lu,
          version: 6,
          modele: modeleDeDepart(),
          typesDocument: DOCUMENTS_DE_DEPART.map((d) => ({ ...d })),
          etapes: etapes.map(({ etape, controleeLe: _c, controleePar: _p, ...reste }) => {
            const [ordre, libelle, icone] = ancien[etape] ?? [9, etape, 'outil'];
            return {
              ...reste,
              ordre,
              libelle,
              icone,
              metier: etape,
              finale: etape === 'reception',
              soumiseLe: null,
            };
          }),
        };
      }
      if (lu.version === 6) {
        etat = lu as unknown as EtatDemo;
        if (version !== 6) enregistrer();
        return etat;
      }
    } catch {
      /* État illisible : on repart du jeu de départ. */
    }
  }
  etat = graine();
  enregistrer();
  return etat;
}

function enregistrer(): void {
  if (!etat) return;
  try {
    stockage()?.setItem(CLE_ETAT, JSON.stringify(etat));
  } catch {
    /* Stockage plein ou bloqué : la démo continue en mémoire. */
  }
}

/** Applique une modification, l'enregistre et prévient les écrans abonnés. */
export function modifierEtat(changer: (brouillon: EtatDemo) => void): void {
  changer(lireEtat());
  enregistrer();
  abonnes.forEach((rappel) => {
    rappel();
  });
}

export function sAbonner(rappel: () => void): () => void {
  abonnes.add(rappel);
  return () => abonnes.delete(rappel);
}

export function reinitialiser(): void {
  const s = stockage();
  if (s) {
    for (let i = s.length - 1; i >= 0; i--) {
      const cle = s.key(i);
      if (cle?.startsWith(CLE_FICHIER)) s.removeItem(cle);
    }
  }
  etat = graine();
  enregistrer();
  abonnes.forEach((rappel) => {
    rappel();
  });
}

// ── Le compte ouvert ─────────────────────────────────────────────────────────

/** `null` : jamais choisi (accès libre, on ouvre le bureau) ; `'aucun'` : déconnecté. */
export function lireCompteCourant(): string | null {
  try {
    return window.sessionStorage.getItem(CLE_COMPTE);
  } catch {
    return null;
  }
}

export function ecrireCompteCourant(id: string): void {
  try {
    window.sessionStorage.setItem(CLE_COMPTE, id);
  } catch {
    /* idem */
  }
}

export function compteCourant(): Utilisateur | null {
  const id = lireCompteCourant();
  if (id === 'aucun') return null;
  const comptes = lireEtat().comptes;
  return comptes.find((c) => c.id === (id ?? 'u-bureau')) ?? comptes[0] ?? null;
}

// ── Journal ──────────────────────────────────────────────────────────────────

/** Ce que ferait le déclencheur `journaliser()` de la base. */
export function journaliser(
  brouillon: EtatDemo,
  action: EntreeJournalDemo['action'],
  objetType: string,
  objetId: string,
  libelle: string,
  avant?: object,
  apres?: object
): void {
  const changements: Record<string, [unknown, unknown]> = {};
  if (action === 'modification' && avant && apres) {
    const a = avant as Record<string, unknown>;
    const b = apres as Record<string, unknown>;
    for (const champ of Object.keys(b)) {
      if (JSON.stringify(a[champ]) !== JSON.stringify(b[champ]))
        changements[champ] = [a[champ], b[champ]];
    }
    if (Object.keys(changements).length === 0) return;
  }
  const dernier = brouillon.journal.reduce((max, e) => Math.max(max, e.id), 0);
  const acteur = compteCourant();
  brouillon.journal.unshift({
    id: dernier + 1,
    horodatage: new Date().toISOString(),
    acteurNom: acteur ? acteur.nom || acteur.email : null,
    action,
    objetType,
    objetId,
    libelle,
    changements,
  });
}

// ── Fichiers ─────────────────────────────────────────────────────────────────

/** Au-delà, un fichier ne tient pas dans le stockage du navigateur. */
export const TAILLE_MAX_DEMO = 1_500_000;

export async function deposerFichier(chemin: string, fichier: File): Promise<void> {
  if (fichier.size > TAILLE_MAX_DEMO) {
    throw new Error(
      'En accès libre, un fichier ne peut dépasser 1,5 Mo. Ce plafond disparaîtra avec Supabase.'
    );
  }
  const contenu = await new Promise<string>((resolve, reject) => {
    const lecteur = new FileReader();
    lecteur.onload = () => {
      resolve(typeof lecteur.result === 'string' ? lecteur.result : '');
    };
    lecteur.onerror = () => {
      reject(new Error('Le fichier n’a pas pu être lu.'));
    };
    lecteur.readAsDataURL(fichier);
  });
  try {
    stockage()?.setItem(CLE_FICHIER + chemin, contenu);
  } catch {
    throw new Error(
      'Le stockage du navigateur est plein. Supprimez un document ou réinitialisez la démo.'
    );
  }
}

export function retirerFichier(chemin: string): void {
  stockage()?.removeItem(CLE_FICHIER + chemin);
}

/**
 * Le fichier déposé, ou un document d'exemple pour ceux du jeu de départ.
 *
 * Rendu en adresse `blob:` : les navigateurs refusent d'ouvrir une adresse
 * `data:` dans un onglet.
 */
export async function adresseFichier(chemin: string, nom: string): Promise<string> {
  const contenu = stockage()?.getItem(CLE_FICHIER + chemin);
  let blob: Blob;
  if (contenu) blob = await (await fetch(contenu)).blob();
  else if (/\.(jpe?g|png|webp|heic)$/i.test(nom)) blob = photoExemple(nom);
  else {
    blob = new Blob(
      [
        `Document de démonstration\n\n${nom}\n\nCe fichier fait partie du jeu de départ de l'accès libre.`,
      ],
      { type: 'text/plain;charset=utf-8' }
    );
  }
  return URL.createObjectURL(blob);
}

/** Une vignette dessinée pour les photos du jeu de départ : un ciel, un toit, des panneaux. */
function photoExemple(nom: string): Blob {
  let teinte = 0;
  for (let i = 0; i < nom.length; i++) teinte = (teinte + nom.charCodeAt(i)) % 40;
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 400 300">
  <rect width="400" height="300" fill="hsl(${195 + teinte} 45% 78%)"/>
  <rect y="190" width="400" height="110" fill="hsl(${90 + teinte} 25% 52%)"/>
  <polygon points="70,190 200,95 330,190" fill="#8a6f5a"/>
  <polygon points="110,170 200,105 290,170" fill="#2f4a6b" stroke="#c9d6e3" stroke-width="2"/>
  <rect x="95" y="190" width="210" height="80" fill="#cbb79e"/>
  <text x="200" y="290" font-family="sans-serif" font-size="13" fill="#1f2a27" text-anchor="middle">Photo de démonstration</text>
</svg>`;
  return new Blob([svg], { type: 'image/svg+xml' });
}

export function nouvelId(prefixe: string): string {
  return `${prefixe}-${crypto.randomUUID().slice(0, 8)}`;
}
